/**
 * Precheck antes de criar um novo lead no Superadmin. Garante as
 * regras de unicidade definidas em 2026-04-23 com o Alexandre:
 *
 *   1. **CNPJ que já virou cliente**: se existe em `clinics` com
 *      `deleted_at is null` (cliente ativo), bloquear criação de
 *      lead — essa empresa já é cliente, não faz sentido duplicar.
 *   2. **Email de lead ativo**: se existe lead com `lower(email)`
 *      igual e `deleted_at is null`, oferecer:
 *        - Abrir lead existente
 *        - Reenviar questionário
 *        - Cancelar
 *
 * CNPJ duplicado **em leads** é permitido (uma clínica pode ter
 * vários médicos como leads separados — conforme decisão do
 * Alexandre). Ver `docs/forms-subsystem.md` §fatiamento M1-01d-0.6.
 *
 * O precheck é uma **query on-demand** (não useQuery contínuo)
 * — consumidor (`NewLeadDialog`) chama antes do submit e decide
 * caminho baseado no resultado. Sem cache duradouro porque a
 * decisão é one-shot: se der "lead existente", o user decide o que
 * fazer e o precheck seguinte será com dados diferentes.
 */
import { z } from 'zod';

import { supabase } from '../../lib/supabase.js';

import { leadStageSchema } from './schemas.js';

/** Lead existente retornado pelo precheck — subset minimal para
 * o dialog de duplicata renderizar. `stage` tipado com o enum do
 * domínio para o `LeadStageBadge` aceitar sem cast. */
const duplicateLeadSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  email: z.string(),
  stage: leadStageSchema,
  clinic_name_guess: z.string().nullable(),
  created_at: z.string(),
});

export type DuplicateLead = z.infer<typeof duplicateLeadSchema>;

/** Clínica-cliente encontrada pelo CNPJ — subset para o dialog
 * "Essa empresa já é cliente". */
const existingClinicSchema = z.object({
  id: z.string().uuid(),
  legal_name: z.string().nullable(),
  trade_name: z.string().nullable(),
  created_at: z.string(),
});

export type ExistingClinic = z.infer<typeof existingClinicSchema>;

/** Resultado possível do precheck. `ok` = seguir com INSERT normal;
 * os outros dois forçam UX específica antes de qualquer write. */
export type LeadPrecheckResult =
  | { kind: 'ok' }
  | { kind: 'already_client'; clinic: ExistingClinic }
  | { kind: 'duplicate_lead'; lead: DuplicateLead };

/** Normaliza CNPJ removendo não-dígitos. Duplicado da função de
 * `formatters.ts` porque este arquivo é focado — evita import
 * transitivo desnecessário. */
function digitsOnly(v: string): string {
  return v.replace(/\D/g, '');
}

export interface LeadPrecheckInput {
  email: string;
  cnpj: string | null;
}

/**
 * Executa o precheck. Retorna um dos 3 resultados. Nunca lança
 * exceção para erros de RLS/rede — em caso de falha, retorna `ok`
 * e deixa o INSERT subsequente falhar (unique index do banco
 * protege como defesa em profundidade).
 */
export async function runLeadPrecheck(input: LeadPrecheckInput): Promise<LeadPrecheckResult> {
  const emailNormalized = input.email.trim().toLowerCase();
  const cnpjDigits = input.cnpj ? digitsOnly(input.cnpj) : '';

  // 1. Cliente com mesmo CNPJ ativo? Bloqueia.
  if (cnpjDigits.length === 14) {
    const { data, error } = await supabase
      .from('clinics')
      .select('id, legal_name, trade_name, created_at')
      .eq('cnpj', cnpjDigits)
      .is('deleted_at', null)
      .maybeSingle();

    if (!error && data) {
      const parsed = existingClinicSchema.safeParse(data);
      if (parsed.success) {
        return { kind: 'already_client', clinic: parsed.data };
      }
    }
  }

  // 2. Lead ativo com mesmo email? Oferece reenviar ou abrir.
  if (emailNormalized.length > 0) {
    const { data, error } = await supabase
      .from('leads')
      .select('id, full_name, email, stage, clinic_name_guess, created_at')
      .ilike('email', emailNormalized)
      .is('deleted_at', null)
      .maybeSingle();

    if (!error && data) {
      const parsed = duplicateLeadSchema.safeParse(data);
      if (parsed.success) {
        return { kind: 'duplicate_lead', lead: parsed.data };
      }
    }
  }

  return { kind: 'ok' };
}
